import {
  Activity,
  CheckCircle2,
  CirclePause,
  CirclePlay,
  ClipboardList,
  Copy,
  DoorClosed,
  DoorOpen,
  ExternalLink,
  Flag,
  Gauge,
  History,
  KeyRound,
  ListChecks,
  LogOut,
  MonitorUp,
  Play,
  Shield,
  Sparkles,
  Trash2,
  Trophy,
  Users,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import { useAuth } from '../providers/AuthProvider';
import type { ArenaEvent, ContestArchive, TeamCode, TeamPublic, TeamState } from '../types/arena';

const statusLabel = { registration: 'ثبت‌نام', countdown: 'شمارش معکوس', running: 'در حال اجرا', paused: 'متوقف', finished: 'پایان‌یافته' } as const;
const statusTone = {
  registration: 'bg-sky-50 text-sky-700 ring-sky-100',
  countdown: 'bg-amber-50 text-amber-700 ring-amber-100',
  running: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  paused: 'bg-orange-50 text-orange-700 ring-orange-100',
  finished: 'bg-slate-100 text-slate-700 ring-slate-200',
} as const;

const flowSteps = [
  { title: 'کد بساز', detail: 'برای هر تیم یک کد تازه بده.' },
  { title: 'تیم‌ها وارد شوند', detail: 'نام و اعضا را همین‌جا چک کن.' },
  { title: 'در ورود را ببند', detail: 'قبل از شروع، تیم تازه اضافه نشود.' },
  { title: 'شروع / مکث', detail: 'تابلو و اتصال تیم‌ها را زیر نظر بگیر.' },
  { title: 'پایان مسابقه', detail: 'نتیجه نهایی شود تا تیم‌ها صفحه نتیجه را ببینند.' },
  { title: 'آرشیو جداگانه', detail: 'وقتی کار تمام شد، log را ذخیره و پنل را خالی کن.' },
] as const;

export function AdminPage({
  event,
  teams,
  states,
  codes,
  archives,
}: {
  event: ArenaEvent;
  teams: TeamPublic[];
  states: Record<string, TeamState>;
  codes: TeamCode[];
  archives: ContestArchive[];
}) {
  const { logout } = useAuth();
  const [busyAction, setBusyAction] = useState('');
  const [notice, setNotice] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [codeLabel, setCodeLabel] = useState('');
  const [newCode, setNewCode] = useState<{ code: string; teamId: string } | null>(null);
  const [copiedCode, setCopiedCode] = useState('');

  const onlineCount = useMemo(
    () =>
      teams.filter((team) => {
        const heartbeat = states[team.id]?.lastHeartbeatMs ?? 0;
        return Date.now() - heartbeat <= 10_000;
      }).length,
    [states, teams],
  );
  const totalCorrect = teams.reduce((sum, team) => sum + team.correctCount, 0);
  const leader = teams[0] ?? null;
  const activeCodes = codes.filter((code) => code.status === 'active').length;

  const action = async (name: string, data: Record<string, unknown>) => {
    setBusyAction(name);
    setNotice(null);
    try {
      const result = await callFunction<Record<string, unknown>, { archivedContestId?: string }>(name, data);
      setNotice({
        type: 'ok',
        text: result.archivedContestId ? 'مسابقه آرشیو شد و پنل برای دوره بعدی آماده است.' : data.action === 'finish' ? 'مسابقه پایان یافت؛ تیم‌ها حالا صفحه نتیجه را می‌بینند.' : 'انجام شد؛ روی تابلو اعمال شد.',
      });
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };

  const control = (controlAction: string) => action('adminControl', { action: controlAction });
  const createTeamCode = async () => {
    setBusyAction('adminCreateTeamCode');
    setNotice(null);
    setNewCode(null);
    try {
      const result = await callFunction<{ label: string }, { code: string; teamId: string }>('adminCreateTeamCode', { label: codeLabel.trim() });
      setNewCode({ code: result.code, teamId: result.teamId });
      setCodeLabel('');
      setNotice({ type: 'ok', text: 'کد آماده شد؛ بدهید دست تیم تا وارد میدان شود.' });
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };
  const codeAction = (codeHash: string, nextStatus: TeamCode['status']) => action('adminTeamCodeAction', { codeHash, action: nextStatus === 'active' ? 'activate' : 'revoke' });
  const copyTeamCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode((current) => (current === code ? '' : current)), 1600);
  };
  const adjustScore = async () => {
    if (!selectedTeam || !delta || reason.trim().length < 5) return;
    await action('adminAdjustScore', { teamId: selectedTeam, delta: Number(delta), reason: reason.trim() });
    setDelta('');
    setReason('');
  };

  return (
    <main className="min-h-screen bg-[#F4F6F8] text-ink">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 px-6 backdrop-blur-xl">
        <div className="mx-auto flex h-[74px] max-w-[1540px] items-center">
          <BrandLockup />
          <div className="mx-6 hidden h-7 w-px bg-slate-200 md:block" />
          <div className="hidden items-center gap-2 text-xs font-black text-slate-500 md:flex">
            <Shield className="size-4 text-brand" /> اتاق فرمان مسابقه
          </div>
          <div className="mr-auto flex items-center gap-3">
            <span className={cn('hidden rounded-full px-3 py-1 text-[10px] font-black ring-1 md:inline-flex', statusTone[event.status])}>{statusLabel[event.status]}</span>
            <Link to="/leaderboard" target="_blank">
              <Button variant="dark" size="sm">
                <MonitorUp className="size-4" /> تابلوی سالن <ExternalLink className="size-3" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => void logout()} aria-label="خروج">
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1540px] px-6 py-6">
        <section className="overflow-hidden rounded-[28px] bg-[#0D1422] text-white shadow-[0_24px_70px_rgba(15,23,42,.18)]">
          <div className="grid gap-0 xl:grid-cols-[1.05fr_.95fr]">
            <div className="relative p-7">
              <div className="absolute inset-0 opacity-35 [background:radial-gradient(circle_at_15%_15%,rgba(242,106,33,.28),transparent_34%),radial-gradient(circle_at_80%_10%,rgba(37,99,235,.20),transparent_36%)]" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/8 px-3 py-1 text-[10px] font-black text-orange-100 ring-1 ring-white/10">
                  <Sparkles className="size-3.5 text-brand" /> مسابقه جاری
                </span>
                <h1 className="mt-5 text-3xl font-black tracking-tight md:text-4xl">DataBinex T-SQL Arena</h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
                  اول کد بساز، تیم‌ها را وارد کن، بازی را شروع کن؛ آخر کار اول «پایان مسابقه» را بزن تا نتیجه برای تیم‌ها نمایش داده شود، بعد اگر خواستی آرشیو کن و مسابقه بعدی را از صفر بساز.
                </p>
                <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <HeroMetric label="وضعیت" value={statusLabel[event.status]} />
                  <HeroMetric label="تیم‌ها" value={teams.length.toLocaleString('fa-IR')} />
                  <HeroMetric label="کد فعال" value={activeCodes.toLocaleString('fa-IR')} />
                  <HeroMetric label="رهبر فعلی" value={leader?.name ?? '—'} />
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 bg-white/[.035] p-5 xl:border-r xl:border-t-0">
              <span className="mb-4 flex items-center gap-2 text-[10px] font-black text-white/45">
                <ClipboardList className="size-4 text-brand" /> راهنمای سریع مدیر
              </span>
              <div className="grid gap-2">
                {flowSteps.map((step, index) => (
                  <div key={step.title} className="flex gap-3 rounded-2xl bg-white/[.045] p-3 ring-1 ring-white/8">
                    <span className={cn('grid size-8 shrink-0 place-items-center rounded-xl text-xs font-black', index <= activeFlowStep(event) ? 'bg-brand text-white' : 'bg-white/8 text-white/40')}>
                      {(index + 1).toLocaleString('fa-IR')}
                    </span>
                    <div>
                      <b className="block text-xs">{step.title}</b>
                      <span className="mt-1 block text-[10px] leading-5 text-white/45">{step.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {event.status === 'registration' && !teams.length && archives.length > 0 && (
          <section className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-7 text-emerald-800">
            مسابقه قبلی در آرشیو ثبت شد. الان پنل تمیز است؛ برای مسابقه جدید از بخش کدهای ورود شروع کن.
          </section>
        )}

        <section className="mt-6 grid gap-4 border-y border-slate-200 py-5 md:grid-cols-4">
          <Metric icon={<Gauge />} label="وضعیت مسابقه" value={statusLabel[event.status]} accent />
          <Metric icon={<Users />} label="تیم‌های ثبت‌شده" value={teams.length.toLocaleString('fa-IR')} />
          <Metric icon={<Wifi />} label="تیم‌های آنلاین" value={`${onlineCount.toLocaleString('fa-IR')} / ${teams.length.toLocaleString('fa-IR')}`} />
          <Metric icon={<Activity />} label="Queryهای کامل" value={totalCorrect.toLocaleString('fa-IR')} />
        </section>

        <section className="grid gap-6 py-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <span className="text-[10px] font-black text-brand">وضعیت زنده</span>
                <h2 className="mt-1 text-2xl font-black">تیم‌ها و سؤال فعال</h2>
              </div>
              <span className="text-xs text-slate-400">هر ثانیه تازه می‌شود</span>
            </div>
            <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
              <div className="grid grid-cols-[1.2fr_100px_130px_100px_100px_150px] border-b border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-400">
                <span>تیم</span><span>اتصال</span><span>سؤال فعلی</span><span>امتیاز ترکیبی</span><span>زنجیره</span><span>عملیات</span>
              </div>
              {teams.map((team) => {
                const state = states[team.id];
                const online = Date.now() - (state?.lastHeartbeatMs ?? 0) <= 10_000;
                return (
                  <div key={team.id} className="grid min-h-[78px] grid-cols-[1.2fr_100px_130px_100px_100px_150px] items-center border-b border-slate-100 px-4 last:border-0 hover:bg-orange-50/30">
                    <div className="flex min-w-0 items-center gap-3">
                      <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-10 rounded-xl" />
                      <div className="min-w-0">
                        <b className="block truncate text-sm">{team.name}</b>
                        <span className="block truncate text-[10px] text-slate-400">{team.members.join('، ')}</span>
                      </div>
                    </div>
                    <span className={cn('inline-flex w-fit items-center gap-2 rounded-full px-2 py-1 text-[10px] font-black', online ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                      {online ? <Wifi className="size-3.5" /> : <WifiOff className="size-3.5" />}{online ? 'متصل' : 'قطع'}
                    </span>
                    <span className="font-mono text-[10px] text-slate-500" dir="ltr">
                      {state?.currentQuestionId ?? '—'}
                      <small className="mt-1 block text-slate-300">{state ? `${toFaNumber(state.currentIndex + 1)} از ${toFaNumber(10)}` : ''}</small>
                    </span>
                    <b className="font-mono text-base">{team.score.toLocaleString('fa-IR')}</b>
                    <b className="font-mono text-sm text-brand">×{team.streak.toLocaleString('fa-IR')}</b>
                    <div className="flex gap-1.5">
                      <Button variant="ghost" size="icon" title="حذف تیم" disabled={busyAction !== ''} onClick={() => void action('adminTeamAction', { teamId: team.id, action: 'remove' })}>
                        <Trash2 className="size-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!teams.length && <div className="grid h-44 place-items-center text-sm text-slate-400">هنوز تیمی برای این مسابقه ثبت نشده است.</div>}
            </div>
          </div>

          <aside className="space-y-5">
            <ControlPanel event={event} busyAction={busyAction} teams={teams} control={control} />
            <TeamCodesPanel
              codes={codes}
              teams={teams}
              busyAction={busyAction}
              codeLabel={codeLabel}
              setCodeLabel={setCodeLabel}
              newCode={newCode}
              copiedCode={copiedCode}
              createTeamCode={createTeamCode}
              codeAction={codeAction}
              copyTeamCode={copyTeamCode}
            />
            <ArchivePanel archives={archives} />
            <ScorePanel
              teams={teams}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              delta={delta}
              setDelta={setDelta}
              reason={reason}
              setReason={setReason}
              busyAction={busyAction}
              adjustScore={adjustScore}
            />
            {notice && <div className={cn('rounded-xl border p-3 text-xs', notice.type === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700')}>{notice.text}</div>}
          </aside>
        </section>
      </div>
    </main>
  );
}

function ControlPanel({ event, busyAction, teams, control }: { event: ArenaEvent; busyAction: string; teams: TeamPublic[]; control: (action: string) => Promise<void> | void }) {
  return (
    <div className="overflow-hidden rounded-[24px] bg-ink p-5 text-white shadow-lift">
      <span className="text-[10px] font-black text-brand">کنترل بازی</span>
      <h2 className="mt-1 text-lg font-black">فرمان‌های اصلی</h2>
      <p className="mt-2 text-[11px] leading-6 text-white/45">پایان مسابقه فقط نتیجه را نهایی می‌کند تا تیم‌ها کارت نتیجه را ببینند. آرشیو و خالی‌کردن پنل یک قدم جداست.</p>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <div className="col-span-2 rounded-2xl border border-white/10 bg-white/[.045] p-2">
          <div className="mb-2 flex items-center justify-between px-1 text-[10px] font-black text-white/45">
            <span>وضعیت ورود تیم‌ها</span>
            <span className={cn('rounded-full px-2 py-0.5', event.admissionOpen ? 'bg-emerald-400/15 text-emerald-200' : 'bg-red-400/15 text-red-200')}>
              {event.admissionOpen ? 'فعال است' : 'غیرفعال است'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="secondary"
              className={cn(event.admissionOpen && 'bg-emerald-500 text-white ring-emerald-300/40 hover:bg-emerald-500')}
              disabled={busyAction !== '' || event.status !== 'registration'}
              onClick={() => !event.admissionOpen && void control('open-admission')}
            >
              <DoorOpen className="size-4" /> فعال
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className={cn(!event.admissionOpen && 'bg-red-500 text-white ring-red-300/40 hover:bg-red-500')}
              disabled={busyAction !== '' || event.status !== 'registration'}
              onClick={() => event.admissionOpen && void control('close-admission')}
            >
              <DoorClosed className="size-4" /> غیرفعال
            </Button>
          </div>
        </div>
        <Button size="sm" variant="secondary" disabled={busyAction !== '' || event.status !== 'registration' || teams.length < 1} onClick={() => void control('start')}>
          <Play className="size-4 fill-current" /> شروع بازی
        </Button>
        <Button size="sm" variant="secondary" disabled={busyAction !== '' || !['running', 'countdown'].includes(event.status)} onClick={() => void control('pause')}>
          <CirclePause className="size-4" /> مکث
        </Button>
        <Button size="sm" variant="secondary" disabled={busyAction !== '' || event.status !== 'paused'} onClick={() => void control('resume')}>
          <CirclePlay className="size-4" /> ادامه
        </Button>
        <Button className="col-span-2" size="sm" variant="danger" disabled={busyAction !== '' || event.status === 'registration' || event.status === 'finished'} onClick={() => void control('finish')}>
          <Flag className="size-4" /> پایان رسمی مسابقه
        </Button>
        <Button className="col-span-2" size="sm" variant="dark" disabled={busyAction !== '' || event.status !== 'finished'} onClick={() => void control('archive')}>
          <History className="size-4" /> آرشیو و آماده‌سازی مسابقه جدید
        </Button>
      </div>
      {event.pausedReason === 'connection' && (
        <div className="mt-4 rounded-xl border border-red-400/20 bg-red-400/8 p-3 text-xs leading-6 text-red-200">
          اتصال یک تیم قطع شده است؛ بازی تا برگشت تیم مکث می‌ماند.
        </div>
      )}
    </div>
  );
}

function TeamCodesPanel({
  codes,
  teams,
  busyAction,
  codeLabel,
  setCodeLabel,
  newCode,
  copiedCode,
  createTeamCode,
  codeAction,
  copyTeamCode,
}: {
  codes: TeamCode[];
  teams: TeamPublic[];
  busyAction: string;
  codeLabel: string;
  setCodeLabel: (value: string) => void;
  newCode: { code: string; teamId: string } | null;
  copiedCode: string;
  createTeamCode: () => Promise<void>;
  codeAction: (codeHash: string, nextStatus: TeamCode['status']) => Promise<void> | void;
  copyTeamCode: (code: string) => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">کدهای ورود</span>
      <h2 className="mt-1 text-lg font-black">کد تازه برای تیم</h2>
      <p className="mt-2 text-[11px] leading-6 text-slate-500">هر کد هویت همان تیم در همین مسابقه است. کدهای جدید بعد از ساخت هم همین‌جا کامل می‌مانند و قابل کپی هستند.</p>
      <div className="mt-4 flex gap-2">
        <Input value={codeLabel} onChange={(event) => setCodeLabel(event.target.value)} placeholder="مثلاً تیم آلفا" />
        <Button size="icon" variant="dark" disabled={busyAction !== ''} onClick={() => void createTeamCode()} aria-label="ساخت کد">
          <KeyRound className="size-4" />
        </Button>
      </div>
      {newCode && (
        <div className="mt-4 rounded-xl border border-brand/25 bg-brand/8 p-3">
          <span className="text-[10px] font-bold text-brand">کدی که باید به تیم بدهید</span>
          <div className="mt-1 flex items-center justify-between gap-3">
            <b className="block font-mono text-lg tracking-widest text-ink" dir="ltr">{newCode.code}</b>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[10px] font-black text-slate-600 ring-1 ring-brand/15 transition hover:bg-orange-50"
              onClick={() => void copyTeamCode(newCode.code)}
            >
              {copiedCode === newCode.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
              {copiedCode === newCode.code ? 'کپی شد' : 'کپی'}
            </button>
          </div>
          <small className="mt-1 block text-[10px] text-slate-400" dir="ltr">{newCode.teamId}</small>
        </div>
      )}
      <div className="mt-4 max-h-72 space-y-2 overflow-auto scrollbar-thin">
        {codes.map((code) => {
          const team = teams.find((item) => item.id === code.teamId);
          const visibleCode = code.code ?? code.codePreview;
          return (
            <div key={code.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <b className="block truncate text-xs">{code.label || team?.name || 'کد بی‌نام'}</b>
                  <span className="mt-1 block font-mono text-sm font-black tracking-wider text-ink" dir="ltr">{visibleCode}</span>
                  {!code.code && <span className="mt-1 block text-[10px] text-amber-600">کد قدیمی است؛ فقط پیش‌نمایش آن ذخیره شده.</span>}
                  {code.code && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600 transition hover:bg-slate-200"
                      onClick={() => void copyTeamCode(code.code!)}
                    >
                      {copiedCode === code.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                      {copiedCode === code.code ? 'کپی شد' : 'کپی کد'}
                    </button>
                  )}
                </div>
                <div className="grid min-w-28 grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'active' && 'bg-emerald-600 text-white hover:bg-emerald-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'active' && void codeAction(code.id, 'active')}
                  >
                    فعال
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'revoked' && 'bg-red-600 text-white hover:bg-red-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'revoked' && void codeAction(code.id, 'revoked')}
                  >
                    غیرفعال
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
                <span>{team ? `استفاده‌شده توسط ${team.name}` : 'آماده برای تیم جدید'}</span>
                <span>{Number(code.redeemCount ?? 0).toLocaleString('fa-IR')} بار ورود</span>
              </div>
            </div>
          );
        })}
        {!codes.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز کدی ساخته نشده است.</div>}
      </div>
    </div>
  );
}

function ArchivePanel({ archives }: { archives: ContestArchive[] }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="flex items-center gap-2 text-[10px] font-black text-brand"><History className="size-4" /> آرشیو مسابقات</span>
      <h2 className="mt-1 text-lg font-black">نتایج دوره‌های قبلی</h2>
      <div className="mt-4 max-h-96 space-y-3 overflow-auto scrollbar-thin">
        {archives.map((archive, index) => (
          <details key={archive.id} className="group rounded-xl border border-slate-200 p-3 open:bg-slate-50">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <b className="flex items-center gap-2 truncate text-xs"><Trophy className="size-4 text-amber-500" /> مسابقه {toFaNumber(archives.length - index)}</b>
                  <span className="mt-1 block text-[10px] text-slate-400">{formatDateTime(archive.finishedAtMs)} · {archive.teamCount.toLocaleString('fa-IR')} تیم</span>
                </div>
                <div className="text-left">
                  <b className="block truncate text-xs">{archive.winnerName ?? 'بدون برنده'}</b>
                  <span className="font-mono text-[10px] text-slate-400">{archive.totalScore.toLocaleString('fa-IR')} امتیاز ترکیبی کل</span>
                </div>
              </div>
            </summary>
            <div className="mt-4 space-y-3 border-t border-slate-200 pt-3">
              <div>
                <span className="mb-2 flex items-center gap-2 text-[10px] font-black text-slate-500"><ListChecks className="size-3.5" /> رتبه‌ها</span>
                <div className="space-y-1.5">
                  {archive.teams.map((team) => (
                    <div key={team.id} className="flex items-center justify-between rounded-lg bg-white px-2 py-2 text-[11px]">
                      <span>{team.finalRank?.toLocaleString('fa-IR') ?? '—'} · {team.name}</span>
                      <span className="font-mono">{team.score.toLocaleString('fa-IR')} امتیاز ترکیبی / {team.correctCount.toLocaleString('fa-IR')} درست</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-2 block text-[10px] font-black text-slate-500">لاگ مدیریتی</span>
                <div className="max-h-32 space-y-1 overflow-auto rounded-lg bg-slate-950 p-2 text-[10px] text-slate-200 scrollbar-thin" dir="ltr">
                  {archive.auditLogs.map((log) => (
                    <div key={log.id}>{new Date(log.createdAtMs).toLocaleTimeString('fa-IR')} · {log.action}{log.teamId ? ` · ${log.teamId.slice(0, 8)}` : ''}</div>
                  ))}
                  {!archive.auditLogs.length && <div dir="rtl">لاگی ثبت نشده است</div>}
                </div>
              </div>
            </div>
          </details>
        ))}
        {!archives.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز مسابقه‌ای آرشیو نشده است.</div>}
      </div>
    </div>
  );
}

function ScorePanel({
  teams,
  selectedTeam,
  setSelectedTeam,
  delta,
  setDelta,
  reason,
  setReason,
  busyAction,
  adjustScore,
}: {
  teams: TeamPublic[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  delta: string;
  setDelta: (value: string) => void;
  reason: string;
  setReason: (value: string) => void;
  busyAction: string;
  adjustScore: () => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">داوری امتیاز</span>
      <h2 className="mt-1 text-lg font-black">تنظیم دستی امتیاز</h2>
      <div className="mt-4 space-y-3">
        <select className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-brand" value={selectedTeam} onChange={(event) => setSelectedTeam(event.target.value)}>
          <option value="">انتخاب تیم</option>
          {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
        </select>
        <Input type="number" value={delta} onChange={(event) => setDelta(event.target.value)} placeholder="مقدار مثبت یا منفی" />
        <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="دلیل تغییر امتیاز (اجباری)" />
        <Button className="w-full" variant="dark" size="sm" disabled={!selectedTeam || !delta || reason.trim().length < 5 || busyAction !== ''} onClick={() => void adjustScore()}>
          ثبت با لاگ داوری
        </Button>
      </div>
    </div>
  );
}

function HeroMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-r border-white/10 pr-3 first:border-r-0 first:pr-0">
      <span className="text-[10px] font-black text-white/35">{label}</span>
      <strong className="mt-1 block truncate text-lg font-black">{value}</strong>
    </div>
  );
}

function Metric({ icon, label, value, accent = false }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return <div className="flex items-center gap-4"><span className={cn('grid size-11 place-items-center rounded-xl', accent ? 'bg-brand text-white' : 'bg-white text-slate-500 ring-1 ring-slate-200')}>{icon}</span><div><span className="text-[10px] font-bold text-slate-400">{label}</span><strong className="block text-xl font-black">{value}</strong></div></div>;
}

function activeFlowStep(event: ArenaEvent) {
  if (event.status === 'registration' && event.admissionOpen) return 1;
  if (event.status === 'registration') return 2;
  if (event.status === 'countdown' || event.status === 'running' || event.status === 'paused') return 3;
  if (event.status === 'finished') return 4;
  return 5;
}

function formatDateTime(value: number) {
  return new Date(value).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
}

function toFaNumber(value: number) {
  return value.toLocaleString('fa-IR');
}
